/**
 * Public API types + defaults of @hf/netcode (ARCHITECTURE §6). Re-exported from `index.ts`.
 *
 * Ownership of the WebRTC plumbing: the client's `OnlineClient` creates the RTCPeerConnection, runs perfect
 * negotiation over `signal` messages, opens the DataChannel and hands the OPEN channel to createWebRtcTransport.
 * Netcode never touches signaling or the WebSocket directly.
 */
import type {
  BinaryChannel,
  DriverStatus,
  EventSink,
  InputFrame,
  MatchDriver,
  QuitReason,
  RollbackGame,
  Transport,
} from '@hf/shared';
import { Btn, HASH_INTERVAL_TICKS, MAX_INPUT_DELAY, deepFreeze } from '@hf/shared';

export const NETCODE_DEFAULTS = deepFreeze({
  /** Local input delay in ticks (0–6, auto-tuned from RTT when `autoInputDelay`). */
  inputDelay: 2,
  minInputDelay: 0,
  maxInputDelay: MAX_INPUT_DELAY,
  autoInputDelay: true,
  /**
   * Auto input delay: ticks of one-way latency we prefer to hide with rollback instead of delay. The latency
   * estimate is the MEDIAN of the last `autoDelayRttWindow` RTT samples: every INPUT carries all unacked inputs,
   * so a late or reordered packet is superseded by the next one a tick later — outliers never delay inputs, and
   * the rollback budget absorbs ordinary jitter. x = rttMedian / 2 / TICK_MS − budget is the fractional delay we
   * want. The first pick is ceil(x − 0.25) once `autoDelayMinSamples` samples exist; after that the delay moves
   * by one only when x leaves the dead band [delay − 1.25, delay + 0.25] (0.5 tick of hysteresis between
   * neighbours) and stays out for `autoDelayUpHoldMs` (up) / `autoDelayDownHoldMs` (down). Never below
   * `minAutoInputDelay`. Result: ≤ 170 ms RTT → 2 (the §6 default), 200 ms → 3–4, 230 ms → 4, 260 ms → 5.
   */
  autoDelayRollbackBudget: 3,
  /**
   * Floor of the AUTO-tuned delay (manual settings may still go down to `minInputDelay`). 2 = the §6 default:
   * at LAN / short RTTs it hides the one-way latency completely (no rollbacks at all), and keeps the opponent's
   * one-shot moments (TOUCHDOWN, SACK…) from being predicted and corrected on every input change.
   */
  minAutoInputDelay: 2,
  /** Auto input delay is re-evaluated this often (ms). */
  autoDelayIntervalMs: 500,
  /** RTT samples (one PONG per `pingIntervalMs`) in the auto-delay percentile window (32 = 8 s). */
  autoDelayRttWindow: 32,
  /** Percentile of that window used as the latency estimate (0.5 = median, see above). */
  autoDelayRttPercentile: 0.5,
  /** RTT samples needed before the first auto delay decision (8 = 2 s, during kickoff). */
  autoDelayMinSamples: 8,
  /** The wanted delay must stay above the dead band this long before the delay is raised (ms). */
  autoDelayUpHoldMs: 3000,
  /** …and below it this long before it is lowered (ms). */
  autoDelayDownHoldMs: 5000,
  /** Max ticks we may predict ahead of the last confirmed remote input before stalling. */
  maxRollback: 10,
  /** Exchange state hashes at confirmed ticks this often. */
  hashInterval: HASH_INTERVAL_TICKS,
  /** Keep this many confirmed snapshots so a desync can be dumped (HASH_DETAIL + JSON) for the mismatching tick. */
  desyncSnapshotTicks: 4 * HASH_INTERVAL_TICKS,
  /** After a hash mismatch, wait this long (ms) for the peer's HASH_DETAIL before reporting with local data only. */
  desyncDetailTimeoutMs: 1500,
  /** Time sync: insert at most one wait frame per this many ticks. */
  timeSyncWaitEvery: 30,
  /**
   * Re-sync after a hitch on the PEER: ticks refused at the prediction barrier were real time this peer lost
   * (the client loop consumed them), so up to this many of them are repaid — one extra tick per accepted
   * `tick()` call, only while time sync says we are behind (smoothed advantage ≤ −0.5) and within
   * `stallRepayWindowTicks` of the last stall. 0 disables.
   */
  maxStallRepayTicks: 12,
  stallRepayWindowTicks: 120,
  /** A new confirmed-state HASH is sent once from confirmation and re-sent by this many pump calls (not on relay). */
  hashResends: 3,
  /** Ping interval for RTT estimation (ms). */
  pingIntervalMs: 250,
  /**
   * Packet pump interval (ms). The session sends acks/inputs/keepalives from its OWN timer, independent of
   * rendering, so a hidden tab (rAF stopped) keeps the connection alive. The loop's ticks still send immediately.
   */
  pumpIntervalMs: 50,
  /**
   * Declare the peer disconnected after this long without packets (ms). Until then a silent peer only makes the
   * session 'stalled', which is fully recoverable: a Wi-Fi roam, a lobby-socket reconnect (relay) or a peer tab
   * that hitches for several seconds resumes the match where it froze. Long enough to ride out real-world blips,
   * short enough that a peer who is really gone ends the match before the player gives up on a frozen field
   * (the client shows "WAITING FOR OPPONENT… n" counting down to this).
   */
  disconnectTimeoutMs: 15000,
  /**
   * Before the HELLO handshake completed, wait this long (ms since `start()`) for the peer instead: the other
   * client may still be loading its match scene.
   */
  handshakeTimeoutMs: 20000,
} as const);

/**
 * Buttons whose presses survive a refused tick (stall / time-sync wait frame): a tap sampled for a tick that
 * `tick()` refused is OR-ed into the next accepted sample, so a short press during a wait frame is never lost.
 * Directions are not carried (holding them is continuous; a stale direction would be worse than a late one).
 */
export const TAP_CARRY_BITS = Btn.U | Btn.D | Btn.A | Btn.S;

/** Timer injection (tests drive it manually; browsers use a Worker-backed interval, Node uses setInterval). */
export interface NetScheduler {
  /** Current time in ms (monotonic). */
  now(): number;
  /** Call `fn` every `ms` until the returned cancel function is called. */
  every(ms: number, fn: () => void): () => void;
}

/** Minimal logger (defaults to `console`). Desync dumps go to `error`. */
export interface NetLogger {
  info(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  error(...args: unknown[]): void;
}

/** Why a session left the `running` family of states. */
export type DisconnectReason =
  /** No packet from the peer for `disconnectTimeoutMs` (or no HELLO since `start()`). */
  | 'timeout'
  /** The peer sent QUIT{QUIT}. */
  | 'quit'
  /** The transport closed underneath us. */
  | 'transport_closed'
  /** HELLO protocol / matchKey / contentHash mismatch (either side). */
  | 'version'
  /** Protocol violation (wrong player index, peer sent QUIT{ERROR}, input window overflow). */
  | 'error'
  /** We called `leave()`. */
  | 'left';

/** Everything known about a detected desync (also logged + dumped to the logger). */
export interface DesyncReport {
  /** Confirmed tick whose state hashes differ. */
  tick: number;
  localHash: number;
  /** The peer's full hash for `tick`, if its HASH packet arrived. */
  remoteHash: number | null;
  /** Local `hashParts` of the snapshot at `tick` (HASH_PART_NAMES order), null if the snapshot is gone. */
  localParts: readonly number[] | null;
  /** The peer's HASH_DETAIL parts, null if they never arrived (timeout). */
  remoteParts: readonly number[] | null;
  /** HASH_PART_NAMES of the sections that differ (empty when either side's parts are unknown). */
  divergentParts: string[];
  /** JSON dump of the local snapshot at `tick` (null if no longer kept). */
  snapshotJson: string | null;
}

/** Cumulative session counters (debug overlay, soak test metrics). */
export interface SessionMetrics {
  /** Ticks advanced (excludes resimulation). */
  ticks: number;
  /** Last confirmed tick (−1 = none). */
  confirmedTick: number;
  /** Number of rollbacks performed. */
  rollbacks: number;
  /** Ticks resimulated in total (sum of rollback depths). */
  resimulatedTicks: number;
  /** Total `game.step` calls (ticks + resimulated ticks). */
  simulatedTicks: number;
  lastRollbackDepth: number;
  maxRollbackDepth: number;
  /** Mean rollback depth over all rollbacks (0 if none). */
  avgRollbackDepth: number;
  /** `tick()` calls refused because the prediction window was full (waiting for the remote). */
  stallTicks: number;
  /** Time-sync wait frames inserted (≤ 1 per `timeSyncWaitEvery` ticks). */
  waitFrames: number;
  /** Extra ticks run to repay time lost at the prediction barrier (peer hitch recovery). */
  repaidTicks: number;
  /** Smoothed (localAdv − remoteAdv) / 2, ticks. */
  frameAdvantage: number;
  inputDelay: number;
  /** Auto-delay latency estimate: median of the recent RTT samples (ms, 0 until measured). */
  autoDelayRttMs: number;
  remoteInputDelay: number;
  delayChanges: number;
  /** Local ticks filled by repeating the last input after a delay increase. */
  repeatedInputs: number;
  /** Sampled local inputs dropped after a delay decrease. */
  skippedInputs: number;
  hashesSent: number;
  /** HASH packets re-sent by the pump (bounded by `hashResends` per hash; 0 on relay transports). */
  hashResends: number;
  hashesCompared: number;
  hashesMatched: number;
  /** Last tick whose hash both peers agreed on (−1 = none). */
  lastVerifiedTick: number;
  packetsMalformed: number;
  /** Well-formed packets rejected as implausible (stale session, ticks outside the window, bad acks). */
  packetsRejected: number;
  /** Remote inputs received more than once (redundancy + duplication). */
  duplicateInputs: number;
  eventsDelivered: number;
  /** Events suppressed because their eventKey was already delivered (resimulation). */
  eventsDeduplicated: number;
  rttMs: number;
  jitterMs: number;
  /** ms since the last valid packet from the peer. */
  msSinceLastPacket: number;
}

export interface RollbackSessionOptions<S, E, C> {
  game: RollbackGame<S, InputFrame, E, C>;
  config: C;
  /** Our player index in the sim (0 or 1). */
  localPlayer: 0 | 1;
  transport: Transport;
  /**
   * Match identity. The HELLO `matchKey` is FNV-1a(matchId) folded with `game.hash(createState(config))`, so two
   * peers only start when they agree on the match AND its config (seed, characters, format…): a rematch on the
   * same matchId with a fresh seed never accepts stale packets of the previous session's handshake.
   */
  matchId: string;
  inputDelay?: number;
  autoInputDelay?: boolean;
  minInputDelay?: number;
  maxInputDelay?: number;
  /** Floor of the auto-tuned delay (default `NETCODE_DEFAULTS.minAutoInputDelay`). */
  minAutoInputDelay?: number;
  maxRollback?: number;
  hashInterval?: number;
  timeSyncWaitEvery?: number;
  maxStallRepayTicks?: number;
  pingIntervalMs?: number;
  pumpIntervalMs?: number;
  disconnectTimeoutMs?: number;
  handshakeTimeoutMs?: number;
  desyncSnapshotTicks?: number;
  desyncDetailTimeoutMs?: number;
  /** Clock + timers. Default: performance.now + a Worker/setInterval timer. Soak tests pass the loopback clock. */
  scheduler?: NetScheduler;
  /** Build fingerprint checked in HELLO. Default `SIM_CONTENT_HASH`. */
  contentHash?: number;
  /**
   * De-duplication key of an event. Default: `${tick}:${type}:${actor}` read from the event object (the
   * `@hf/sim` `eventKey` format; the stepped tick is used when the event has no `tick`).
   */
  eventKey?: (event: E) => string;
  /**
   * Events for which this returns true are CONFIRMED-ONLY: they are not delivered from predicted ticks at all.
   * The session holds them per tick (a rollback discards and re-collects them) and delivers them through
   * `drainEvents` once their tick is confirmed — exactly the events of the confirmed timeline, each exactly once,
   * one confirmation latency later. Use it for score / game-flow / announcer events (TOUCHDOWN, INTERCEPTION…)
   * that must never be called and then retracted; keep instant feedback (kick, bounce, jump) predicted.
   */
  confirmedOnly?: (event: E) => boolean;
  /**
   * Per-section hashes for HASH_DETAIL (`@hf/sim` `hashStateParts`), written in HASH_PART_NAMES order.
   * Default: the full hash in part 0 and zeros elsewhere.
   */
  hashParts?: (state: S, out: Uint32Array) => void;
  /** Match over? Evaluated on CONFIRMED states only; true → status 'ended' (the session stops advancing). */
  isGameOver?: (state: S) => boolean;
  /** Desync detected at a confirmed tick (after HASH_DETAIL was exchanged when possible). */
  onDesync?: (tick: number, localHash: number, remoteHash: number) => void;
  /** Full desync diagnostics (fires right before `onDesync`). */
  onDesyncReport?: (report: DesyncReport) => void;
  /** Defaults to `console`. */
  logger?: NetLogger;
  /**
   * Optional sink receiving EVERY event of every simulated tick (resimulation included, BEFORE de-duplication) —
   * debugging only. The session itself always collects events while resimulating (RollbackGame.step) and
   * delivers each `eventKey` once through `drainEvents`.
   */
  debugEvents?: EventSink<E>;
}

/**
 * Why the last `tick()` call returned false: `'waiting'` (not started / handshake pending), `'stall'` (prediction
 * barrier: the peer is behind — this time is repaid later, see `maxStallRepayTicks`), `'wait'` (time-sync wait
 * frame: we are ahead on purpose), `'over'` (ended / desynced / disconnected / disposed). Null after an accepted tick.
 */
export type TickRefusal = 'waiting' | 'stall' | 'wait' | 'over';

/**
 * A rollback session is a MatchDriver plus online-only controls. `timePolicy` is 'catch_up' (never drop ticks);
 * `onConfirmed` fires for confirmed ticks only.
 *
 * `tick(localInputs)`: pass `[p0, p1]` (the session reads index `localPlayer`) or `[mine]` (length 1).
 */
export interface RollbackSession<S, E> extends MatchDriver<S, E> {
  readonly localPlayer: 0 | 1;
  /** Current local input delay (ticks). */
  readonly inputDelay: number;
  /** Why the session stopped (null while waiting/running/stalled). */
  readonly disconnectReason: DisconnectReason | null;
  /** Set once a desync was detected and diagnosed. */
  readonly desyncReport: DesyncReport | null;
  /** Why the last `tick()` returned false (null when it advanced). Diagnostics / client loop policy. */
  readonly lastRefusal: TickRefusal | null;
  /** Begin exchanging packets (after HELLO handshake / match_start). Starts the packet pump. */
  start(): void;
  /**
   * Change input delay live (settings). Never rewrites sent ticks: increasing repeats the last input, decreasing
   * skips sampled ticks (see @hf/shared packet.ts). Disables auto-tuning (an explicit user choice wins).
   */
  setInputDelay(ticks: number): void;
  /** Graceful leave: sends QUIT (redundantly) to the peer; status becomes 'ended'. Does NOT close the transport. */
  leave(reason?: QuitReason): void;
  /** Status transitions (waiting → running ⇄ stalled → desynced/disconnected/ended). */
  onStatusChange(handler: (status: DriverStatus) => void): () => void;
  metrics(): SessionMetrics;
}

export type CreateRollbackSession = <S, E, C>(opts: RollbackSessionOptions<S, E, C>) => RollbackSession<S, E>;

/** Wrap an OPEN, client-owned RTCDataChannel ({ordered:false, maxRetransmits:0}). Closing the transport closes it. */
export type CreateWebRtcTransport = (dc: RTCDataChannel) => Transport;
/**
 * Wrap the OnlineClient's relay channel (`OnlineClient.relayChannel(matchId)`). Closing the transport only
 * unsubscribes; the WebSocket belongs to the OnlineClient.
 */
export type CreateRelayTransport = (channel: BinaryChannel) => Transport;

export interface LoopbackOptions {
  /** One-way base latency, ms. */
  latencyMs: number;
  /** Uniform jitter ± ms. */
  jitterMs: number;
  /** Drop probability 0..1. */
  loss: number;
  /** Probability 0..1 that a packet is delayed extra to arrive out of order. */
  reorder: number;
  /** PRNG seed (deterministic network conditions). */
  seed: number;
  /** Probability 0..1 that a packet is delivered twice (default 0). */
  duplicate?: number;
}

export const LOOPBACK_PERFECT: LoopbackOptions = deepFreeze({
  latencyMs: 0,
  jitterMs: 0,
  loss: 0,
  reorder: 0,
  seed: 1,
  duplicate: 0,
});

/** Per-direction loopback counters. */
export interface LoopbackLinkStats {
  sent: number;
  dropped: number;
  duplicated: number;
  delivered: number;
}

/**
 * Two connected loopback transports sharing a MANUAL clock: nothing is delivered until `advance(ms)` moves time
 * forward, which also fires the scheduler's timers. Soak tests step both sessions + `advance(TICK_MS)` in a loop:
 * fully deterministic, no fake timers, no real time.
 */
export interface LoopbackPair {
  readonly a: Transport;
  readonly b: Transport;
  /** Pass to both sessions' `scheduler`. */
  readonly scheduler: NetScheduler;
  advance(ms: number): void;
  /** Change network conditions live (e.g. `{loss: 1}` = cable pulled). Default direction: both. */
  setConditions(opts: Partial<Omit<LoopbackOptions, 'seed'>>, direction?: 'a->b' | 'b->a' | 'both'): void;
  /** Packets currently in flight. */
  readonly inFlight: number;
  readonly linkStats: { readonly aToB: LoopbackLinkStats; readonly bToA: LoopbackLinkStats };
}

export type CreateLoopbackPair = (opts?: Partial<LoopbackOptions>) => LoopbackPair;
